/**
 * The deal list's half of register P1.
 *
 * Chris counted three numbers on three screens — Emailed Intake twenty-two, the
 * Ingestion Queue five, the deal list thirteen — and called the spread one
 * "duplicates alone do not explain." Intake got its answer: `IntakeAnalyticsStrip`
 * leads with one number and `ReconciliationSurfaces` states every difference.
 * The deal list, the screen the thirteen was counted on, still showed a bare
 * count derived client-side from `/loans/` with nothing beside it.
 *
 * A number that another screen contradicts, with no reason on screen, is the
 * defect. So this states the arithmetic where the count is read:
 *
 *   * how many live pipeline rows exist, from the same server predicate
 *     `list_loans` uses — not re-derived here, because two predicates that
 *     agree today drift next release;
 *   * how many of them intake put there, and how many arrived some other way
 *     (entered by hand, migrated, or created before the mailbox existed);
 *   * how many records intake holds in total;
 *   * and, when the desk's own view filters are hiding rows, that it is the
 *     view doing it — the one difference the server cannot know about.
 *
 * It renders nothing at all on a demo account or for a user without
 * `intake:view`. A pipeline user who cannot read intake does not need an error
 * about a reconciliation they never asked for, and a demo account must never
 * show a live figure beside sample deals.
 */
import { useQuery } from '@tanstack/react-query'

import ReconciliationSurfaces from '@/components/shared/ReconciliationSurfaces'
import { ingestionService, type IntakeReconciliation } from '@/services/ingestion'
import {
  PIPELINE_RECONCILIATION_DOM_ID,
  dealListSurface,
  reconciliationClauses,
} from './reconciliation'

export interface PipelineReconciliationProps {
  /** Rows the grid is showing right now, after the desk's view filters. */
  onScreen: number
  /** Demo accounts show no live figures rather than invented ones. */
  live: boolean
  /** Drop the row chrome when a host row already draws it. This component
   *  renders nothing on a demo account, on error and while loading, so the
   *  host must own the row — otherwise the toolbar sharing it would vanish
   *  along with the line. */
  bare?: boolean
}

export default function PipelineReconciliation({ onScreen, live, bare = false }: PipelineReconciliationProps) {
  // Same key as the intake strip on purpose: one fetch serves both screens, and
  // a promotion that invalidates `['intake-reconciliation']` refreshes this too.
  const { data, isError } = useQuery<IntakeReconciliation>({
    queryKey: ['intake-reconciliation'],
    queryFn: () => ingestionService.getReconciliation(),
    enabled: live,
    // A 403 is the expected answer for a pipeline user without `intake:view`,
    // not a transient failure. Retried and re-polled it would be three wasted
    // requests on mount and two more every thirty seconds, forever, for a line
    // that will never render. Both surfaces this mounts on are reachable
    // without intake permission, so the query has to stop asking.
    retry: false,
    refetchInterval: (query) => (query.state.status === 'error' ? false : 30_000),
  })

  // Silent on demo, on error, and while loading. This line explains a count the
  // page already shows correctly; a spinner or an error banner in its place
  // would be louder than the thing it annotates.
  if (!live || isError || !data) return null

  const dealList = dealListSurface(data.surfaces)
  if (!dealList) return null

  return (
    <div className={bare ? '' : 'border-b border-amber-300/10 px-4 py-1'}>
      <ReconciliationSurfaces
        total={data.total}
        surfaces={data.surfaces}
        domId={PIPELINE_RECONCILIATION_DOM_ID}
        label="Why each screen shows a different number"
        lead={
          <p className="text-[10px] text-stone-500">
            {reconciliationClauses({ onScreen, total: data.total, dealList }).join(' · ')}
          </p>
        }
      />
    </div>
  )
}
